import React, { JSX, use, useEffect, useMemo, useState } from 'react';
import { getDeliveryKey, getDeliveryUrl } from 'lib/util';
import PersonResult from 'components/molecules/PersonResult';
import {
  GET_FACULTY_TYPES,
  GET_ALL_FACULTY,
  GET_FILTERED_FACULTY,
} from '../r2/graphql/GET_FACULTY_STAFF';
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  useQuery,
} from '@apollo/client';
import ThemeContext from 'components/context/ThemeContext';
import {
  Faculty,
  FacultyType,
  TitleMapping,
} from 'components/types/FacultyTypes';
import CursorPagination from 'components/r2/CursorPagination';
import getOsfViewportSize from 'assets/utils/getOsfViewportSize';
import Icons from 'components/atoms/Icons';
import {
  SetControlPanelType,
  ToggleMobileControlsType,
} from 'components/types/FilterTypes';
import { StyleguideComponentProps } from 'lib/component-props';

type FacultySearchState = {
  controlPanel: string;
  mobileControlsOpen: boolean;
  lastViewportSize: string;
};

const initialState = {
  controlPanel: '',
  mobileControlsOpen: false,
  lastViewportSize: '',
};

const FacultyStaffSearch = (props: StyleguideComponentProps): JSX.Element => {
  const [searchState, setSearchState] = useState({ ...initialState });
  const [filter, setFilter] = useState('');
  const [filterName, setFilterName] = useState('All');
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [cursorHistory, setCursorHistory] = useState<string[]>(['']);
  const [jumpLoading, setJumpLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const isFiltered = filterName !== 'All';
  const themeContext = use(ThemeContext);
  const theme = themeContext?.themeState?.theme;
  const pageSize = 10;

  const FACULTY_TYPES_FOLDER = '9F43115B-24CA-4DC8-9DAE-BA70963B49D4';
  const FACULTY_TYPE_TEMPLATE = '4CB5585E-7FEC-457A-89CC-DA4A57F5AE3F';
  const FACULTY_TEMPLATE = '840CA178-2DF1-4F39-BB83-1A92106EA2FD';
  const SAMC_ITEM = '6CCFC2FF-D299-4923-BC46-FD476DB4D7BE';
  const SFMC_ITEM = 'F017A02B-B1AE-401E-A3AE-AB09318B4B9C';
  const itemPath = theme?.includes('sacon') ? SAMC_ITEM : SFMC_ITEM;

  const samc_title = 'Saint Anthony College of Nursing';
  const sfmc_title = 'Saint Francis Medical Center College of Nursing';
  const samc_title_mapping: TitleMapping = {
    faculty: `${samc_title} Faculty`,
    staff: `${samc_title} Staff`,
    administration: `${samc_title} Administration`,
    boardmembers: `${samc_title} Board Members`,
    all: `${samc_title} Directory`,
  };
  const sfmc_title_mapping = {
    faculty: `${sfmc_title} Faculty`,
    staff: `${sfmc_title} Staff`,
    administration: `${sfmc_title} Administration`,
    boardmembers: `${sfmc_title} Board Members`,
    all: `${sfmc_title} Directory`,
  };

  const baseVars = {
    path: itemPath,
    pageSize,
    template: FACULTY_TEMPLATE,
    after: endCursor,
  };

  const variables = isFiltered
    ? {
        ...baseVars,
        filterValue: filter,
      }
    : baseVars;

  const { data: facultyTypes } = useQuery(GET_FACULTY_TYPES, {
    variables: { path: FACULTY_TYPES_FOLDER, template: FACULTY_TYPE_TEMPLATE },
  });

  const facultyTypeResults: Array<FacultyType> =
    facultyTypes && facultyTypes.search.results;

  const activeQuery = useMemo(() => {
    if (isFiltered) return GET_FILTERED_FACULTY;
    return GET_ALL_FACULTY;
  }, [isFiltered]);

  const { data: facultySearchResults, loading } = useQuery(activeQuery, {
    variables,
    fetchPolicy: 'network-only',
    skip: !theme,
  });

  const facultyResults: Array<Faculty> =
    facultySearchResults && facultySearchResults.search.results;

  const totalPages = Math.ceil(
    (facultySearchResults?.search?.total || 0) / pageSize
  );

  const filterKey = JSON.stringify({
    type: filter ? filter : 'All',
  });

  const handleNext = () => {
    const nextCursor = facultySearchResults?.search?.pageInfo?.endCursor;
    if (nextCursor) {
      setCursorHistory(prev => [...prev, nextCursor]);
      setEndCursor(nextCursor);
      setPage(prev => prev + 1);
      scrollToTop();
    }
  };

  const handlePrevious = () => {
    if (cursorHistory.length > 1) {
      const newHistory = [...cursorHistory];
      newHistory.pop();
      const prevCursor = newHistory[newHistory.length - 1] || null;
      setCursorHistory(newHistory);
      setEndCursor(prevCursor);
      setPage(prev => Math.max(prev - 1, 1));
      scrollToTop();
    }
  };

  const handlePageClick = async (targetPage: number) => {
    if (targetPage === page) return;

    if (targetPage < page) {
      const newHistory = cursorHistory.slice(0, targetPage);
      const newCursor = newHistory[newHistory.length - 1] || null;
      setCursorHistory(newHistory);
      setEndCursor(newCursor);
      setPage(targetPage);
      scrollToTop();
      return;
    }
    setJumpLoading(true);
    const newHistory = [...cursorHistory];
    let tempCursor = endCursor;

    for (let i = page; i < targetPage; i++) {
      const result = await client.query({
        query: activeQuery,
        variables: {
          ...variables,
          after: tempCursor,
        },
        fetchPolicy: 'network-only',
      });

      tempCursor = result.data?.search?.pageInfo?.endCursor;
      if (tempCursor) newHistory.push(tempCursor);
      else break;
    }

    setCursorHistory(newHistory);
    setEndCursor(newHistory[targetPage - 1] || null);
    setPage(targetPage);
    setJumpLoading(false);
    scrollToTop();
  };

  const handleFilters = (description: string, id?: string) => {
    setFilterName(description);
    id && setFilter(id);
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const isDesktopView = () => {
    const viewport = getOsfViewportSize();
    return viewport === 'lg' || viewport === 'xl';
  };

  const closeMobileControls = () => {
    const bottomOffset = parseInt(document.body.style.bottom);
    document.body.classList.remove('body-fixed');
    document.body.style.removeProperty('bottom');
    window.scroll(0, -bottomOffset);
    document.body.classList.remove('mobile-controls-open');

    setSearchState(prevFields => ({
      ...prevFields,
      mobileControlsOpen: false,
    }));
  };

  const setControlPanel: SetControlPanelType = (control: string) => {
    const { lastViewportSize } = searchState;

    if (!(lastViewportSize === 'lg' || lastViewportSize === 'xl')) {
      toggleMobileControls(control);
    } else {
      setSearchState((prevState: FacultySearchState) => ({
        ...prevState,
        controlPanel: control,
      }));
    }
  };

  const toggleMobileControls: ToggleMobileControlsType = (control: string) => {
    const { controlPanel, mobileControlsOpen } = searchState;
    if (mobileControlsOpen) {
      if (controlPanel === control) {
        closeMobileControls();
      } else {
        setSearchState(prevFields => ({
          ...prevFields,
          controlPanel: control,
        }));
      }
    } else {
      openMobileControls(control);
    }
    setTimeout(() => {
      resetElementFocusMobile(mobileControlsOpen);
    }, 100);
  };

  const openMobileControls = (control: string) => {
    if (!(getOsfViewportSize() === 'lg' || getOsfViewportSize() === 'xl')) {
      const bottomOffset = `-${window.pageYOffset}px`;
      document.body.classList.add('body-fixed');
      document.body.classList.add('mobile-controls-open');
      document.body.style.bottom = bottomOffset;

      setSearchState(prevFields => ({
        ...prevFields,
        controlPanel: control,
        mobileControlsOpen: true,
      }));
    }
  };

  const resetElementFocusMobile = (onLoad: boolean) => {
    if (!(getOsfViewportSize() === 'lg' || getOsfViewportSize() === 'xl')) {
      if (onLoad) {
        // for accessibility purposes, we need to move the results container element to the top of the DOM so it is read in the same order as it is visually
        let parent = document.querySelector('main');
        let titleBar = document.querySelector('.title-bar');
        let resultsContainer = document.querySelector(
          '.search-page-controls .results-controls'
        );
        resultsContainer && parent?.insertBefore(resultsContainer, titleBar);
      }
    }
  };

  useEffect(() => {
    setEndCursor(null);
    setCursorHistory(['']);
    setPage(1);
  }, [filterKey, activeQuery]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        setIsDesktop(isDesktopView);
      });
    }
  });

  return (
    <div
      className="overhauled"
      id={`faculty-staff-search-${props.rendering?.uid}`}
    >
      <div className="faculty-staff-search">
        <div className="search-page-controls providers only-mobile">
          <div className="faculty-control-toggles">
            <div className="control-toggle-btns">
              <div className="mob-filter-wrapper faculty-filter-sidebar">
                <h1 className="h1-headline banner-title">
                  {theme?.includes('sacon')
                    ? samc_title_mapping[
                        filterName
                          .replace(/\s/g, '')
                          .toLowerCase() as keyof TitleMapping
                      ]
                    : sfmc_title_mapping[
                        filterName
                          .replace(/\s/g, '')
                          .toLowerCase() as keyof TitleMapping
                      ]}
                </h1>
                <div className="mob-filter">
                  <button
                    className={`btn-primary btn-fw filter-label ${searchState.controlPanel === 'filters' ? 'active' : ''}`}
                    aria-label="Open filters"
                    onClick={() => setControlPanel('filters')}
                  >
                    Browse By Role
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="jss-inner-container">
          <div className="faculty-staff-wrapper search-page-wrapper faculty">
            <div className="left-col">
              <div className="filter-bar-wrapper">
                <div className="faculty-filter-sidebar">
                  <div className="filter-title-bar">
                    <div
                      className="close-button only-mobile"
                      onClick={() => closeMobileControls()}
                    >
                      <Icons id="close" className="icon icon-close" />
                    </div>
                  </div>
                  <h3 className="filters-header filter-label bg-primary-dark osf-intro-copy">
                    Browse By Role
                  </h3>
                  <div className="filters">
                    {facultyTypeResults &&
                      facultyTypeResults?.map((facultyType: FacultyType) => {
                        return (
                          <div className="filter">
                            <input
                              type="checkbox"
                              name={facultyType.Description.value}
                              value={facultyType.id}
                              onChange={() =>
                                handleFilters(
                                  facultyType.Description.value,
                                  facultyType.id
                                )
                              }
                              checked={
                                filterName === facultyType.Description.value
                              }
                            ></input>
                            <label
                              htmlFor={facultyType.Description.value}
                              className="osf-body-copy"
                            >
                              {facultyType.Description.value}
                            </label>
                          </div>
                        );
                      })}
                    <div className="filter">
                      <input
                        type="checkbox"
                        name="All"
                        value="All"
                        defaultChecked={true}
                        checked={filterName === 'All'}
                        onChange={() => handleFilters('All')}
                      ></input>
                      <label htmlFor="All" className="osf-body-copy">
                        All
                      </label>{' '}
                    </div>
                  </div>
                  {searchState.mobileControlsOpen ? (
                    <button
                      className="btn-primary btn-fw no-icon only-mobile"
                      onClick={() => closeMobileControls()}
                    >
                      Update Results
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="right-col">
              <h1 className="h1-headline no-mobile">
                {theme?.includes('sacon')
                  ? samc_title_mapping[
                      filterName
                        .replace(/\s/g, '')
                        .toLowerCase() as keyof TitleMapping
                    ]
                  : sfmc_title_mapping[
                      filterName
                        .replace(/\s/g, '')
                        .toLowerCase() as keyof TitleMapping
                    ]}
              </h1>
              {jumpLoading || loading ? (
                <div className="text-center">Loading...</div>
              ) : facultySearchResults?.search.total > 0 ? (
                <div className="results">
                  {facultyResults &&
                    facultyResults.map((result: Faculty) => {
                      return (
                        <PersonResult
                          keyProp={result.id}
                          faculty={result}
                          isDesktop={isDesktop}
                        />
                      );
                    })}
                </div>
              ) : (
                <p className="no-results">No results.</p>
              )}
              <div className="pagination-container">
                {facultySearchResults &&
                  facultySearchResults.search?.total > pageSize && (
                    <CursorPagination
                      onNext={handleNext}
                      onPrevious={handlePrevious}
                      hasNext={facultySearchResults?.search?.pageInfo?.hasNext}
                      page={page}
                      totalPages={totalPages}
                      onPageClick={handlePageClick}
                    />
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Apollo Client Setup
const deliveryKey = getDeliveryKey();
const endpoint = getDeliveryUrl();
const client = new ApolloClient({
  link: new HttpLink({
    uri: endpoint,
    headers: { sc_apikey: deliveryKey },
  }),
  cache: new InMemoryCache(),
});

const WrappedFacultyStaffSearch = (props: any) => (
  <ApolloProvider client={client}>
    <FacultyStaffSearch {...props} />
  </ApolloProvider>
);

export default WrappedFacultyStaffSearch;
